import { Alert, Card, Col, ConfigProvider, DatePicker, Divider, Form, Row, Select, Statistic, Typography } from "antd";
import SDIModal from "../../../../components/ui/SDIModal";
import PropTypes from "prop-types";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../../../variables/api/axiosInstance";
import AvenantAgencySelector from "./AvenantAgencySelector";
import { applyAvenantAgenceCommission } from "../../../../services/avenantCommissionService";
import { rightsService } from "../../../../services/rightsService";

const { Text, Title } = Typography;
const { Option } = Select;

const RetraitVehicule = (props) => {
    const [modalRetrait, setModalRetrait] = useState(props.open);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    // États pour les données du contrat et du retrait
    const [contratFlotte, setContratFlotte] = useState(null);
    const [vehiculesEligibles, setVehiculesEligibles] = useState([]);
    const [vehiculeSelectionne, setVehiculeSelectionne] = useState(null);
    const [dateEffetRetrait, setDateEffetRetrait] = useState(dayjs());
    const [preview, setPreview] = useState(null);
    const [fraisAccessoires, setFraisAccessoires] = useState(0);
    // Agence ciblée + taux (siège) à appliquer au contrat résultant de l'avenant
    const [avCom, setAvCom] = useState({ agence: null, taux: null, intermediaire: null });
    // HCO081 (antidatage) : autorise une date de retrait dans le PASSÉ (bornée au début du terme).
    const canAntidate = rightsService("HCO081");

    useEffect(() => {
        setModalRetrait(props.open);
        if (props.open && props?.contrat?.codeId) {
            console.log("Contrat flotte pour retrait:", props?.contrat);
            setContratFlotte(props?.contrat);

            // Filtrer les véhicules éligibles (2/3 roues, non sinistrés)
            const vehiculesEligibles = props?.contrat?.engins?.filter(engin =>
                isVehiculeEligible(engin)
            ) || [];

            setVehiculesEligibles(vehiculesEligibles);

            // Récupérer les frais accessoires pour le retrait
            axiosInstance.get(`/api/avenant/accessoires/RT/${props?.contrat?.engins[0]?.categorie?.codeCategorie}`)
                .then(response => {
                    setFraisAccessoires(response.data);
                })
                .catch(error => {
                    console.error("Erreur lors de la récupération des frais accessoires:", error);
                });
        }
    }, [props.open, props?.contrat?.codeId]);

    const { CarOutlined, DollarOutlined, WarningOutlined } = require('@ant-design/icons');

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(value);
    };

    const isVehiculeEligible = (engin) => {
        // Vérifier qu'il n'est pas sinistré
        return !engin.compteurSinistre;
    };

    const handleVehiculeChange = (vehiculeId) => {
        const vehicule = vehiculesEligibles.find(v => v.codeId === vehiculeId);
        setVehiculeSelectionne(vehicule);

        // Calculer le preview de ristourne
        if (vehicule) {
            calculatePreview(vehicule);
        }
    };

    const calculatePreview = async (vehicule) => {
        if (!vehicule || !dateEffetRetrait) return;

        try {
            const payload = {
                contratCodeId: contratFlotte.codeId,
                enginCodeId: vehicule.codeId,
                dateEffet: dateEffetRetrait.format('YYYY-MM-DD'),
                dateRetrait: dateEffetRetrait.format('YYYY-MM-DD')
            };

            const response = await axiosInstance.put('/api/contrat/retrait-preview', payload);
            console.log("Preview retrait:", response.data);
            setPreview(response.data);
        } catch (error) {
            console.error("Erreur lors du calcul du preview:", error);
        }
    };

    useEffect(() => {
        if (vehiculeSelectionne && dateEffetRetrait) {
            calculatePreview(vehiculeSelectionne);
        }
    }, [dateEffetRetrait, vehiculeSelectionne]);

    const navigate = useNavigate();

    const enterLoading = () => setLoading(true);
    const outLoading = () => setLoading(false);

    const handleRetraitSubmit = async () => {
        try {
            await form.validateFields();

            // Vérifications d'éligibilité
            if (!isContratEligible()) {
                toast.error("Ce contrat n'est pas éligible au retrait de véhicule");
                return;
            }

            if (!vehiculeSelectionne) {
                toast.error("Veuillez sélectionner un véhicule à retirer");
                return;
            }

            enterLoading();

            const payload = {
                contratCodeId: contratFlotte.codeId,
                enginCodeId: vehiculeSelectionne.codeId,
                dateRetrait: dateEffetRetrait.format('YYYY-MM-DD'),
                ristourne: preview?.ristourne || 0,
                accessoires: fraisAccessoires,
                taxes: (fraisAccessoires - preview?.primeNette) * 0.06 || 0,
                primeNette: - preview?.primeNette || 0,
                primeTotale: (fraisAccessoires - preview?.primeNette) * 1.06 || 0 };

            console.log("Payload retrait:", payload);

            const response = await axiosInstance.put(
                `/api/contrat/retrait-vehicule`,
                payload
            );

            await applyAvenantAgenceCommission(response.data?.codeId, avCom.agence, avCom.taux, avCom.intermediaire);
            toast.success("Retrait de véhicule effectué avec succès!");
            setModalRetrait(false);
            props.update(false);
            navigate(`/details-contrat/${response.data.codeId}`, { replace: true });
            window.location.reload();

        } catch (error) {
            console.error(error);
            toast.error("Erreur lors du retrait du véhicule");
        } finally {
            outLoading();
        }
    };

    const handleCancel = () => {
        setModalRetrait(false);
        props.update(false);
    };

    const isContratEligible = () => {
        return contratFlotte?.engins && contratFlotte?.engins.length > 1 &&
            vehiculesEligibles.length > 0;
    };

    const getNombreVehiculesRestants = () => {
        return (contratFlotte?.engins?.length || 0) - 1;
    };

    return (
        <SDIModal
            title="RETRAIT DE VÉHICULE DE LA FLOTTE"
            centered
            confirmLoading={loading}
            open={modalRetrait}
            onOk={handleRetraitSubmit}
            onCancel={handleCancel}
            width={1100}
            zIndex={1940}
            okText="Effectuer le retrait"
            cancelText="Annuler"
            >
                <ConfigProvider locale={frFR}>
                    <div className="retrait-content">
                        <AvenantAgencySelector
                            organismeCode={props?.contrat?.organisme?.code}
                            defaultAgence={props?.contrat?.courtier || props?.contrat?.producteur?.agence || props?.contrat?.intermediaire?.agence}
                            defaultIntermediaire={props?.contrat?.intermediaire}
                            categorie={props?.contrat?.engins?.[0]?.categorie?.codeCategorie}
                            onChange={(ag, t, inter) => setAvCom({ agence: ag, taux: t, intermediaire: inter })}
                        />

                    {/* Informations sur le contrat flotte */}
                    {contratFlotte && (
                        <Card className="mb-4" size="small" title="Contrat flotte">
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Statistic
                                        title="Numéro de police"
                                        value={contratFlotte.numeroPoliceP || contratFlotte.numeroPoliceM}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Nombre de véhicules actuels"
                                        value={contratFlotte.engins?.length || 0}
                                        prefix={<CarOutlined />}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Véhicules éligibles au retrait"
                                        value={vehiculesEligibles.length}
                                        valueStyle={{ color: vehiculesEligibles.length > 0 ? '#3f8600' : '#cf1322' }}
                                    />
                                </Col>
                            </Row>
                        </Card>
                    )}

                    {/* Conditions d'éligibilité */}
                    {!isContratEligible() ? (
                        <Alert
                            message="Contrat non éligible au retrait"
                            description={
                                contratFlotte?.engins && contratFlotte?.engins?.length > 1
                                    ? "Le retrait de véhicule n'est possible que sur les contrats flotte."
                                    : vehiculesEligibles.length === 0
                                        ? "Aucun véhicule éligible trouvé. Seuls les véhicules à 2 ou 3 roues non sinistrés peuvent être retirés."
                                        : "Le contrat doit être actif pour effectuer un retrait."
                            }
                            type="error"
                            icon={<WarningOutlined />}
                            showIcon
                            className="mb-4"
                        />
                    ) : null}

                    {/* Règles du retrait */}
                    {/* <Card className="mb-4" size="small" title="Règles du retrait de véhicule">
                        <div className="info-section">
                            <div className="info-item">
                                <InfoCircleOutlined className="info-icon info" />
                                <Text>
                                    <Text strong>Code avenant :</Text> RT + Année + 001 (premier retrait de l'année)
                                </Text>
                            </div>
                            <div className="info-item">
                                <CarOutlined className="info-icon warning" />
                                <Text>
                                    <Text strong>Véhicules éligibles :</Text> Uniquement les engins à 2 ou 3 roues non sinistrés
                                </Text>
                            </div>
                            <div className="info-item">
                                <DeleteOutlined className="info-icon error" />
                                <Text>
                                    <Text strong>Effet :</Text> Résiliation des garanties du véhicule retiré avec ristourne au prorata
                                </Text>
                            </div>
                        </div>
                    </Card> */}

                    <Form form={form} layout="vertical">
                        {/* Sélection du véhicule à retirer */}
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Véhicule à retirer"
                                    name="vehiculeARetirer"
                                    rules={[{ required: true, message: 'Veuillez sélectionner un véhicule' }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder="Rechercher par immatriculation…"
                                        onChange={handleVehiculeChange}
                                        style={{ width: '100%' }}
                                        // Recherche par immatriculation / marque / type (utile pour les
                                        // flottes de 100+ engins où le scroll est fastidieux).
                                        optionFilterProp="label"
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {vehiculesEligibles.map(vehicule => (
                                            <Option
                                                key={vehicule.codeId}
                                                value={vehicule.codeId}
                                                label={`${vehicule.immatriculation || ''} ${vehicule.marque || ''} ${vehicule.type || ''}`.trim()}
                                            >
                                                <div className="vehicule-option">
                                                    <Text strong>{vehicule.immatriculation}</Text>
                                                    <Text type="secondary" className="ml-2">
                                                        {vehicule.marque} - {vehicule.type}
                                                    </Text>
                                                </div>
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Date d'effet du retrait"
                                    name="dateEffetRetrait"
                                    rules={[{ required: true, message: 'Veuillez sélectionner une date' }]}
                                    initialValue={dateEffetRetrait}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                        value={dateEffetRetrait}
                                        onChange={setDateEffetRetrait}
                                        disabledDate={(current) => {
                                            // Borne HAUTE = échéance du contrat.
                                            const dateEcheance = contratFlotte ? dayjs(contratFlotte.dateEcheance) : dayjs().add(1, 'year');
                                            // Borne BASSE : sans HCO081, pas de date passée (aujourd'hui) ;
                                            // avec HCO081 (antidatage), on autorise le passé mais jamais
                                            // avant la date d'effet du terme (le retrait ne peut précéder
                                            // le début de la couverture).
                                            const dateEffet = contratFlotte?.dateEffet ? dayjs(contratFlotte.dateEffet).startOf('day') : null;
                                            const plancher = canAntidate
                                                ? dateEffet
                                                : dayjs().startOf('day');
                                            return current && (
                                                (plancher && current < plancher) ||
                                                current > dateEcheance
                                            );
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* Détails du véhicule sélectionné */}
                        {vehiculeSelectionne && (
                            <Card className="mb-4" size="small" title="Véhicule à retirer">
                                <Row gutter={16}>
                                    <Col span={6}>
                                        <div className="vehicle-info">
                                            <Text strong>Immatriculation:</Text>
                                            <Text className="ml-2">{vehiculeSelectionne.immatriculation}</Text>
                                        </div>
                                    </Col>
                                    <Col span={6}>
                                        <div className="vehicle-info">
                                            <Text strong>Marque:</Text>
                                            <Text className="ml-2">{vehiculeSelectionne.marque}</Text>
                                        </div>
                                    </Col>
                                    <Col span={6}>
                                        <div className="vehicle-info">
                                            <Text strong>Type:</Text>
                                            <Text className="ml-2">{vehiculeSelectionne.type}</Text>
                                        </div>
                                    </Col>
                                    <Col span={6}>
                                        <div className="vehicle-info">
                                            <Text strong>N° Chassis:</Text>
                                            <Text className="ml-2">{vehiculeSelectionne.numeroChassis}</Text>
                                        </div>
                                    </Col>
                                </Row>
                            </Card>
                        )}

                        {/* Impact sur la flotte */}
                        {vehiculeSelectionne && (
                            <Card className="mb-4" size="small" title="Impact sur la flotte">
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Statistic
                                            title="Véhicules avant retrait"
                                            value={contratFlotte?.engins?.length || 0}
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic
                                            title="Véhicules après retrait"
                                            value={getNombreVehiculesRestants()}
                                            valueStyle={{ color: '#52c41a' }}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic
                                            title="Type d'opération"
                                            value="Résiliation partielle"
                                            valueStyle={{ color: '#fa8c16' }}
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        )}

                        {/* Motif du retrait */}
                        {/* <Form.Item
                            label="Motif du retrait"
                            name="motifRetrait"
                            rules={[{ required: true, message: 'Veuillez indiquer le motif du retrait' }]}
                        >
                            <TextArea
                                value={motifRetrait}
                                onChange={(e) => setMotifRetrait(e.target.value)}
                                placeholder="Indiquez la raison du retrait (ex: vente du véhicule, mise hors service, changement d'usage, etc.)"
                                rows={3}
                            />
                        </Form.Item> */}

                        {/* Récapitulatif financier */}
                        {preview && (
                            <Card className="cost-summary" size="small">
                                <Title level={5}>
                                    <DollarOutlined className="mr-2" />
                                    Récapitulatif financier du retrait
                                </Title>
                                <div className="cost-breakdown">
                                    <Row justify="space-between" className="cost-line">
                                        <Col>
                                            <Text>Ristourne au prorata:</Text>
                                        </Col>
                                        <Col>
                                            <Text strong className="text-green-600">
                                                {formatCurrency(preview.primeNette || 0)}
                                            </Text>
                                        </Col>
                                    </Row>

                                    <Row justify="space-between" className="cost-line">
                                        <Col>
                                            <Text>Frais accessoires:</Text>
                                        </Col>
                                        <Col>
                                            <Text strong>{formatCurrency(fraisAccessoires)}</Text>
                                        </Col>
                                    </Row>

                                    <Divider className="my-2" />

                                    <Row justify="space-between" className="cost-line total-cost">
                                        <Col>
                                            <Text strong className="text-lg">
                                                {(preview.primeNette - fraisAccessoires) >= 0 ? "MONTANT À REMBOURSER:" : "MONTANT À PAYER:"}
                                            </Text>
                                        </Col>
                                        <Col>
                                            <Text
                                                strong
                                                className={`text-lg ${(preview.primeNette - fraisAccessoires) >= 0 ? 'text-green-600' : 'text-blue-600'}`}
                                            >
                                                {formatCurrency(Math.abs(preview.primeNette - fraisAccessoires))}
                                            </Text>
                                        </Col>
                                    </Row>
                                </div>
                            </Card>
                        )}
                    </Form>
                </div>
            </ConfigProvider>

            <style jsx>
                {`
                    .retrait-content {
                        max-height: 70vh;
                        overflow-y: auto;
                    }
                    
                    .info-section {
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }
                    
                    .info-item {
                        display: flex;
                        align-items: flex-start;
                        gap: 8px;
                    }
                    
                    .info-icon {
                        margin-top: 2px;
                        font-size: 16px;
                    }
                    
                    .info-icon.warning {
                        color: #faad14;
                    }
                    
                    .info-icon.info {
                        color: #1890ff;
                    }
                    
                    .info-icon.error {
                        color: #ff4d4f;
                    }
                    
                    .vehicule-option {
                        display: flex;
                        align-items: center;
                    }
                    
                    .vehicle-info {
                        margin-bottom: 8px;
                    }
                    
                    .cost-summary {
                        background-color: #f8f9fa;
                        border: 1px solid #e9ecef;
                    }
                    
                    .cost-breakdown .cost-line {
                        margin-bottom: 8px;
                    }
                    
                    .cost-breakdown .total-cost {
                        background-color: #e6f7ff;
                        padding: 8px;
                        border-radius: 4px;
                        margin-top: 8px;
                    }
                    
                    .ml-2 { margin-left: 8px; }
                    .mr-2 { margin-right: 8px; }
                    .mb-4 { margin-bottom: 16px; }
                    .my-2 { margin: 8px 0; }
                    .text-lg { font-size: 16px; }
                    .text-blue-600 { color: #1890ff; }
                    .text-green-600 { color: #52c41a; }
                `}
            </style>
        </SDIModal>
    );
};

RetraitVehicule.propTypes = {
    open: PropTypes.bool,
    update: PropTypes.func,
    contrat: PropTypes.object
};

export default RetraitVehicule;
